// frontend/src/modules/email/pages/TemplatesTab.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { useApi } from '../../../design-system/useApi';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { statusPill, statusDot } from '../../../design-system/visual';
import { emailApi } from '../api';
import type { EmailTemplate } from '../types';

export function TemplatesTab() {
  const { data, loading, reload } = useApi<{ templates: EmailTemplate[] }>('/api/email/templates');
  const [openKey, setOpenKey] = React.useState<string | null>(null);

  if (loading) return <LoadingState variant="inline" />;
  const templates = data?.templates ?? [];

  return (
    <div className="grid gap-2 max-w-4xl">
      {templates.map((t) => (
        <TemplateRow
          key={t.template_key}
          template={t}
          open={openKey === t.template_key}
          onToggle={() => setOpenKey((k) => k === t.template_key ? null : t.template_key)}
          onChanged={reload}
        />
      ))}
    </div>
  );
}

interface RowProps {
  template: EmailTemplate;
  open: boolean;
  onToggle: () => void;
  onChanged: () => void;
}

function TemplateRow({ template, open, onToggle, onChanged }: RowProps) {
  const confirm = useConfirm();
  const [subject, setSubject] = React.useState(template.subject);
  const [body, setBody]       = React.useState(template.body_html);
  const [saving, setSaving]   = React.useState(false);

  React.useEffect(() => { setSubject(template.subject); setBody(template.body_html); }, [template]);

  async function save() {
    setSaving(true);
    try {
      await emailApi.updateTemplate(template.template_key, { subject, body_html: body });
      toast.success('Template saved');
      onChanged();
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally { setSaving(false); }
  }

  async function reset() {
    if (!(await confirm({ title: 'Reset this template to the default?', description: 'Your custom version will be discarded.', destructive: true, confirmLabel: 'Reset' }))) return;
    try {
      await emailApi.resetTemplate(template.template_key);
      toast.success('Reset to default');
      onChanged();
    } catch (err: any) { toast.error('Reset failed', { description: err.message }); }
  }

  return (
    <div className="rounded-2xl ring-1 ring-border/40 bg-card overflow-hidden">
      <button type="button" onClick={onToggle}
        className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-muted/40 transition-colors">
        <div>
          <p className="text-[14px] font-semibold tracking-[-0.005em] text-foreground">{prettyKey(template.template_key)}</p>
          <p className="text-[11.5px] font-mono text-muted-foreground mt-0.5">{template.template_key}</p>
        </div>
        {template.is_override
          ? <span className={statusPill('info')}><span className={statusDot('info')} />Customized</span>
          : <span className={statusPill('muted')}><span className={statusDot('muted')} />Default</span>}
      </button>
      {open && (
        <div className="px-5 pb-5 grid gap-4 border-t border-border/40 pt-5">
          <div className="grid gap-1.5">
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label>Body (HTML)</Label>
            <Textarea rows={10} className="font-mono text-[12px]" value={body} onChange={(e) => setBody(e.target.value)} />
            <p className="text-[12px] text-muted-foreground">Use <code className="font-mono">{'{{variable}}'}</code> for placeholders.</p>
          </div>
          <div className="flex justify-end gap-2">
            {template.is_override && (
              <Button variant="outline" type="button" className="rounded-full h-10 px-4 text-[13px] font-semibold" onClick={reset}>Reset to default</Button>
            )}
            <Button type="button" disabled={saving} onClick={save} className="rounded-full h-10 px-5 text-[13px] font-semibold">{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function prettyKey(k: string): string {
  return k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
